import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import ServiceCard from "@/components/ServiceCard";
import { ArrowRight, Shield, Zap, Webhook, Server, HardDrive, Cpu, Globe2 } from "lucide-react";

const HERO_IMG = "/images/hero.png";

const STATS = [
  { k: "24", v: "DATACENTERS" },
  { k: "30+", v: "OS TEMPLATES" },
  { k: "99.95%", v: "UPTIME SLA" },
  { k: "<12s", v: "BOOT TIME" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setFeatured(r.data.slice(0, 6))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-zinc-800">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Cloud servers"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/40" />
          <div className="absolute inset-0 scanlines" />
        </div>
        <div className="relative grain">
          <div className="container-pad pt-24 md:pt-32 pb-24 md:pb-32 grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-7">
              <div className="overline">// CLOUD SERVER MANAGEMENT · v1.0</div>
              <h1 className="mt-5 font-display font-light tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
                Rent, deploy, and manage <span className="text-signal font-medium">cloud servers</span>
                <br />
                from a single dashboard.
              </h1>
              <p className="mt-6 max-w-xl text-zinc-400 text-base md:text-lg leading-relaxed">
                CloudRent is a self-service hosting platform. Pick a VPS or a dedicated machine,
                pay securely, and receive your SSH credentials by email. Resize, snapshot,
                and rebuild anytime from your dashboard.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/pricing" data-testid="hero-cta-buy" className="btn-signal h-11 px-6 text-sm font-mono tracking-widest">
                  BUY NOW — FROM $4/MO <ArrowRight size={16} />
                </Link>
                <Link to="/catalog" data-testid="hero-cta-explore" className="btn-ghost h-11 px-6 text-sm font-mono tracking-widest">
                  EXPLORE SERVERS
                </Link>
              </div>

              {/* Stats strip */}
              <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 divide-x divide-zinc-800 border border-zinc-800 rounded-sm bg-black/60 backdrop-blur-sm">
                {STATS.map((s) => (
                  <div key={s.v} className="p-4">
                    <div className="font-mono text-2xl text-white">{s.k}</div>
                    <div className="overline mt-1">{s.v}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right side — IDE-styled provisioning card */}
            <div className="md:col-span-5 hidden md:block">
              <div className="card-flat overflow-hidden">
                <div className="flex items-center gap-1.5 border-b border-zinc-800 px-3 py-2">
                  <span className="h-2 w-2 rounded-full bg-zinc-700" />
                  <span className="h-2 w-2 rounded-full bg-zinc-700" />
                  <span className="h-2 w-2 rounded-full bg-zinc-700" />
                  <span className="ml-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                    sh — provision.sh
                  </span>
                </div>
                <pre className="font-mono text-[12.5px] leading-6 text-zinc-300 p-5 whitespace-pre-wrap">
{`$ cloudrent buy titan-m \\
    --region fra-1 \\
    --image ubuntu-24.04

> payment verified ✓
> webhook signature verified ✓
> provisioning vm node-3f29ac.cloudrentvps.com
> SSH ready in 8.2s

`}
                  <span className="text-signal">credentials.txt</span>
                  {` -> dashboard
$ ssh root@node-3f29ac.cloudrentvps.com
Welcome to Ubuntu 24.04 LTS
Last login: now from your dashboard`}
                </pre>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE SELL */}
      <section className="border-b border-zinc-800">
        <div className="container-pad py-14 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="overline">// WHAT WE SELL</div>
            <h2 className="mt-3 font-display text-3xl tracking-tight">
              Cloud servers <span className="text-signal">& developer APIs</span>
            </h2>
            <p className="mt-3 text-zinc-400 text-sm leading-relaxed">
              Two product lines, one checkout. VPS and dedicated hosting plus a small
              library of production-ready REST APIs. All recurring, software-only
              subscriptions. No physical goods are shipped.
            </p>
          </div>
          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { Icon: Server, t: "VPS HOSTING", d: "Virtual private servers with dedicated CPU, RAM, and NVMe storage.", b: "SAAS" },
              { Icon: HardDrive, t: "STORAGE & MEMORY", d: "Specialized VPS plans tuned for backups, databases, and caches.", b: "SAAS" },
              { Icon: Cpu, t: "DEDICATED METAL", d: "Bare-metal servers with full IPMI access and a hardware-replacement SLA.", b: "SAAS" },
              { Icon: Globe2, t: "DEVELOPER APIS", d: "REST APIs for market data, OCR, transactional email, and IP geolocation.", b: "SAAS" },
            ].map((x) => (
              <div key={x.t} className="card-flat p-5">
                <span className="badge-mono">{x.b}</span>
                <x.Icon size={18} className="text-signal mt-3" />
                <div className="mt-2 font-mono text-[11px] uppercase tracking-widest text-white">{x.t}</div>
                <p className="mt-2 text-xs text-zinc-400 leading-relaxed">{x.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURE STRIP */}
      <section className="border-b border-zinc-800">
        <div className="container-pad grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-zinc-800">
          {[
            { Icon: Shield, t: "SECURE BILLING", d: "Card details are entered on our certified checkout provider's environment — never on our servers." },
            { Icon: Webhook, t: "AUDITED EVENTS", d: "Every billing event is signature-verified and written to an immutable internal audit log before any server is provisioned." },
            { Icon: Zap, t: "INSTANT DELIVERY", d: "SSH credentials are emailed and appear in your dashboard the moment payment clears." },
          ].map(({ Icon, t, d }) => (
            <div key={t} className="p-8 flex gap-4">
              <div className="h-9 w-9 inline-flex items-center justify-center bg-zinc-900 border border-zinc-800 rounded-sm">
                <Icon size={16} className="text-signal" />
              </div>
              <div>
                <div className="font-mono text-[11px] uppercase tracking-widest text-white">{t}</div>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVER TYPES */}
      <section className="container-pad py-20 md:py-28">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline">// SERVER TYPES</div>
            <h2 className="mt-3 font-display text-3xl md:text-4xl tracking-tight">
              From a $4 edge node to <span className="text-signal">EPYC bare metal.</span>
            </h2>
          </div>
          <Link to="/catalog" data-testid="see-all-link" className="hidden md:inline-flex items-center gap-2 font-mono text-xs tracking-widest text-zinc-400 hover:text-white">
            BROWSE ALL <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <Link
            to="/catalog?subtype=vps"
            data-testid="category-vps"
            className="md:col-span-8 card-flat overflow-hidden hover:border-zinc-600 transition-colors relative group"
          >
            <div className="absolute inset-0 opacity-30 group-hover:opacity-40 transition-opacity">
              <img src="/images/servers.png" alt="VPS Hosting" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
            </div>
            <div className="relative p-8 md:p-12 min-h-[320px] flex flex-col justify-between">
              <div className="flex items-center gap-2">
                <Server size={18} className="text-signal" />
                <span className="badge-mono">CATEGORY · 01</span>
              </div>
              <div>
                <h3 className="font-display text-3xl md:text-4xl tracking-tight">VPS HOSTING</h3>
                <p className="mt-3 max-w-md text-zinc-400 text-sm">
                  KVM virtual machines on AMD EPYC nodes with NVMe storage. General-purpose,
                  storage-optimized, memory-optimized, and CPU-optimized line-ups.
                </p>
              </div>
            </div>
          </Link>

          <Link
            to="/catalog?subtype=dedicated"
            data-testid="category-dedicated"
            className="md:col-span-4 card-flat overflow-hidden hover:border-zinc-600 transition-colors relative group"
          >
            <div className="absolute inset-0 opacity-30 group-hover:opacity-40 transition-opacity">
              <img src="/images/apis.png" alt="Dedicated servers" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/70 to-black" />
            </div>
            <div className="relative p-8 md:p-10 min-h-[320px] flex flex-col justify-between">
              <div className="flex items-center gap-2">
                <Cpu size={18} className="text-signal" />
                <span className="badge-mono">CATEGORY · 02</span>
              </div>
              <div>
                <h3 className="font-display text-3xl tracking-tight">DEDICATED</h3>
                <p className="mt-3 text-zinc-400 text-sm">
                  Single-tenant bare metal with IPMI, ECC memory and a hardware-replacement SLA.
                </p>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* FEATURED CARDS */}
      <section className="container-pad pb-24">
        <div className="overline mb-6">// FEATURED · {featured.length} PLANS</div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {featured.map((s, i) => (
            <ServiceCard
              key={s.id}
              svc={s}
              span={i % 5 === 0 ? "md:col-span-8" : "md:col-span-4"}
            />
          ))}
        </div>
      </section>
    </div>
  );
}
